import { UnknownError, ValueOf } from '@omnea-digital/toolbox'
import { UseQueryResult, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import {
  Address,
  Currency,
  Department,
  LineItemType,
  PaymentMethod,
  PaymentTerm,
  Subsidiary,
} from '@omnea-app/core-data/coreDataFrontendClient'
import { CoreDataHttpClient } from '@omnea-app/core-data/coreDataHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { CamelSystemCoreDataType, formatEntityType } from '@omnea-app/legacy/utils/core-data/formatDependencyDataType'

export type CamelCoreDataOptionMap = {
  address: Address
  currency: Currency
  department: Department
  lineItemType: LineItemType
  paymentMethod: PaymentMethod
  paymentTerm: PaymentTerm
  subsidiary: Subsidiary
}
export type SystemCoreDataOption = ValueOf<CamelCoreDataOptionMap>

interface CoreDataFetcherProps {
  coreDataClient: CoreDataHttpClient
  queryParams?: { includeDependenciesMetadata?: boolean; includeArchived?: boolean }
}

type CoreDataFetchers = {
  [Type in CamelSystemCoreDataType]: (props: CoreDataFetcherProps) => Promise<CamelCoreDataOptionMap[Type][]>
}

const fetchSystemCoreDataByType = async <T extends CamelSystemCoreDataType>({
  coreDataClient,
  coreDataType,
  queryParams,
}: CoreDataFetcherProps & { coreDataType: T }): Promise<CamelCoreDataOptionMap[T][]> => {
  const entityTypeKebab = formatEntityType(coreDataType, 'kebab')
  const { data, error, response } = await coreDataClient.GET('/system-core-data/{entityType}', {
    params: { path: { entityType: entityTypeKebab }, query: queryParams },
  })
  if (!isNil(error) || !response.ok) {
    throw new UnknownError({ message: response.statusText })
  }
  const responseKey = formatEntityType(coreDataType, 'camelPlural')
  const items = (data as Record<string, unknown>)[responseKey]
  if (!Array.isArray(items)) {
    throw new UnknownError({ message: 'Invalid response shape' })
  }
  return items
}

const createFetcher =
  <T extends CamelSystemCoreDataType>(coreDataType: T) =>
  (props: CoreDataFetcherProps): Promise<CamelCoreDataOptionMap[T][]> =>
    fetchSystemCoreDataByType({ ...props, coreDataType })

export const coreDataFetchers: CoreDataFetchers = {
  address: createFetcher('address'),
  department: createFetcher('department'),
  subsidiary: createFetcher('subsidiary'),
  paymentMethod: createFetcher('paymentMethod'),
  paymentTerm: createFetcher('paymentTerm'),
  currency: createFetcher('currency'),
  lineItemType: createFetcher('lineItemType'),
}

const systemCoreDataQueryKey = 'systemCoreData'
export const getSystemCoreDataQueryKey = (
  type: CamelSystemCoreDataType | null,
  includeDependenciesMetadata?: boolean,
  includeArchived?: boolean
) => [systemCoreDataQueryKey, String(type), includeDependenciesMetadata ?? false, includeArchived ?? false]

const fetchSystemCoreData = async <T extends CamelSystemCoreDataType>({
  getToken,
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
}: {
  getToken: () => Promise<string>
  coreDataType: T
  includeDependenciesMetadata: boolean
  includeArchived: boolean
}): Promise<CamelCoreDataOptionMap[T][]> => {
  const authorization = await getToken()
  const coreDataClient = new CoreDataHttpClient({ authorization })
  const queryParams = { includeDependenciesMetadata, includeArchived }
  const allResults = await coreDataFetchers[coreDataType]({ coreDataClient, queryParams })
  return allResults
}

// Overloaded function signatures for better type inference
export function useSystemCoreData<Type extends CamelSystemCoreDataType>({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  staleTime?: number
}): UseQueryResult<CamelCoreDataOptionMap[Type][], Error>
export function useSystemCoreData({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  staleTime,
}: {
  coreDataType: null
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  staleTime?: number
}): UseQueryResult<never[], Error>
export function useSystemCoreData<Type extends CamelSystemCoreDataType | null>({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  staleTime?: number
}): UseQueryResult<Type extends CamelSystemCoreDataType ? CamelCoreDataOptionMap[Type][] : never[], Error>
export function useSystemCoreData<Type extends CamelSystemCoreDataType | null>({
  coreDataType,
  includeDependenciesMetadata = false,
  includeArchived = false,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  staleTime?: number
}) {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: getSystemCoreDataQueryKey(coreDataType, includeDependenciesMetadata, includeArchived),
    queryFn: async () => {
      if (isNil(coreDataType)) return []

      return fetchSystemCoreData({
        getToken,
        coreDataType,
        includeDependenciesMetadata,
        includeArchived,
      })
    },
    staleTime: staleTime ?? 30 * 1000, // Default to 30 seconds
  })
}

export const useSystemCoreDataSuspense = <Type extends CamelSystemCoreDataType>({
  coreDataType,
  includeDependenciesMetadata = false,
  includeArchived = false,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
}): CamelCoreDataOptionMap[Type][] => {
  const { getToken } = useAuthenticatedUser()

  return useSuspenseQuery({
    queryKey: getSystemCoreDataQueryKey(coreDataType, includeDependenciesMetadata, includeArchived),
    queryFn: async () =>
      fetchSystemCoreData({
        getToken,
        coreDataType,
        includeDependenciesMetadata,
        includeArchived,
      }),
  }).data
}
